/**
 * Pure business rules for the Provision card (claude-code-UI-prompt.md golden rule #3 — no
 * business logic in components). No React, no Redux, no fetch — everything here is a plain
 * function so it can be unit tested in isolation, mirroring
 * src/domain/professionalContacts/professionalContacts.rules.js.
 */
import { getFormattedDate } from "../../Utils/Helpers/dateUtils";
import {
  MIN_SCENARIOS,
  MAX_SCENARIOS,
  DCF_ALLOWED_CONTENT_TYPES,
  DCF_ALLOWED_EXTENSIONS,
  DCF_MAX_SIZE_BYTES,
  DCF_DOCUMENT_CATEGORY_NAME,
  MONEY_DECIMAL_PLACES,
} from "./provision.types";

// ---------------------------------------------------------------------------------------
// Edit gate (DFXX1) — trust the API's `editable` flag, never re-derive CRR client-side.
// ---------------------------------------------------------------------------------------

/**
 * @param {import("./provision.types").Provision|null|undefined} provision
 * @returns {boolean}
 */
export function isProvisionEditable(provision) {
  return Boolean(provision && provision.editable);
}

// ---------------------------------------------------------------------------------------
// Money / percent formatting & validation (DFXX2, DFXX4, DFXX9)
// ---------------------------------------------------------------------------------------

/**
 * @param {number|string|null|undefined} value
 * @returns {boolean} true when empty (optional) or a valid GBP amount, MONEY_DECIMAL_PLACES dp
 */
export function isValidMoney(value) {
  if (value === null || value === undefined || value === "") return true;
  const num = Number(value);
  if (Number.isNaN(num)) return false;
  const factor = 10 ** MONEY_DECIMAL_PLACES;
  return Math.round(num * factor) === num * factor;
}

/**
 * @param {number|string|null|undefined} value
 * @returns {boolean} true when empty (optional) or 0.00-100.00, 2dp
 */
export function isValidPercent(value) {
  if (value === null || value === undefined || value === "") return true;
  const num = Number(value);
  if (Number.isNaN(num)) return false;
  if (num < 0 || num > 100) return false;
  return Math.round(num * 100) === num * 100;
}

/** @param {number|null|undefined} value @returns {string} "0.00" style, "" when nullish */
export function formatMoney(value) {
  if (value === null || value === undefined || value === "") return "";
  return Number(value).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** @param {number|null|undefined} value @returns {string} "0.00" style, "" when nullish (no % suffix — callers add it) */
export function formatPercent(value) {
  if (value === null || value === undefined || value === "") return "";
  return Number(value).toFixed(2);
}

// ---------------------------------------------------------------------------------------
// Scenarios — 2-5 rule, sequential labels (DFXX9), auto-calculated outcome (DFXX10)
// ---------------------------------------------------------------------------------------

/** @param {import("./provision.types").Scenario[]|null|undefined} scenarios */
export function canAddScenario(scenarios) {
  return (scenarios?.length || 0) < MAX_SCENARIOS;
}

/** @param {import("./provision.types").Scenario[]|null|undefined} scenarios */
export function canRemoveScenario(scenarios) {
  return (scenarios?.length || 0) > MIN_SCENARIOS;
}

/**
 * @param {number} count current scenario count
 * @returns {string} the "Add up to N more…"/"Maximum reached" hint the mockup shows
 */
export function scenarioCountHint(count) {
  if (count >= MAX_SCENARIOS) return `Maximum of ${MAX_SCENARIOS} scenarios reached.`;
  const remaining = MAX_SCENARIOS - count;
  return `You can add up to ${remaining} more scenario${remaining > 1 ? "s" : ""} (maximum ${MAX_SCENARIOS} total).`;
}

/**
 * @param {import("./provision.types").ScenarioInput} draft
 * @returns {{valid: boolean, missing: string[]}}
 */
export function validateScenarioDraft(draft) {
  const missing = [];
  if (!isValidPercent(draft?.provisionPct)) missing.push("Provision must be 0.00-100.00%");
  if (!isValidPercent(draft?.weightingPct)) missing.push("Weighting must be 0.00-100.00%");
  return { valid: missing.length === 0, missing };
}

// ---------------------------------------------------------------------------------------
// Upload DCF — allowed types/size (DFXX11)
//
// These checks are the ONLY enforcement of the type and size limits. The file goes straight to
// the Document Helper and the API only ever receives a JSON reference to it, so the server can
// validate the reported extension but can never verify the actual bytes or size. Treat a change
// here as a change to a business rule, not a UX nicety.
// ---------------------------------------------------------------------------------------

/**
 * The lower-cased extension including its leading dot, or "" when the name has none.
 * @param {string|null|undefined} fileName
 * @returns {string}
 */
export function fileExtensionOf(fileName) {
  if (!fileName) return "";
  const dot = fileName.lastIndexOf(".");
  if (dot <= 0) return "";
  return fileName.slice(dot).toLowerCase();
}

/** @param {string|null|undefined} fileName */
export function isAllowedDcfExtension(fileName) {
  return DCF_ALLOWED_EXTENSIONS.includes(fileExtensionOf(fileName));
}

/** @param {{type?: string}|null|undefined} file a browser File */
export function isAllowedDcfContentType(file) {
  return Boolean(file && DCF_ALLOWED_CONTENT_TYPES.includes(file.type));
}

/** @param {{size?: number}|null|undefined} file a browser File */
export function isDcfFileTooLarge(file) {
  return Boolean(file && file.size > DCF_MAX_SIZE_BYTES);
}

/**
 * @param {File|null|undefined} file
 * @returns {?string} an error message, or null when the file is acceptable
 */
export function validateDcfFile(file) {
  if (!file) return null;
  // Extension first, because that is what the API now validates on the reference we send it —
  // rejecting here on a rule the server doesn't apply (or vice versa) would be a mismatch. The
  // browser's `type` is only a secondary check: it is empty for some legitimate Office files
  // depending on OS/browser, so an empty type must not fail a file with an allowed extension.
  if (!isAllowedDcfExtension(file.name)) {
    return "Unsupported file type. Allowed: PDF, Excel, Word.";
  }
  if (file.type && !isAllowedDcfContentType(file)) {
    return "Unsupported file type. Allowed: PDF, Excel, Word.";
  }
  if (isDcfFileTooLarge(file)) {
    return `File is too large. Maximum size is ${Math.round(DCF_MAX_SIZE_BYTES / (1024 * 1024))} MB.`;
  }
  return null;
}

/**
 * The Document Helper storage reference for a DCF file —
 * "<loanId>/<DCF_DOCUMENT_CATEGORY_NAME>/<fileName>" — computed client-side the same way
 * DocumentsDashboard.jsx and buildQuoteDocumentFilePath already do for every other Loan Document
 * (none of them rely on the Document Helper's upload response for the path). The storage account
 * and parent container are resolved server-side from the `type`/`subdomain` headers, not here.
 * @param {string} loanId
 * @param {string} fileName
 * @returns {string}
 */
export function buildDcfDocumentFilePath(loanId, fileName) {
  return `${loanId}/${DCF_DOCUMENT_CATEGORY_NAME}/${fileName}`;
}

// ---------------------------------------------------------------------------------------
// Professionals — 4th party conditional reveal (DFXX16-19)
// ---------------------------------------------------------------------------------------

/**
 * Company / Contact / Contact Number are shown only once a 4th-party role is chosen.
 * @param {import("./provision.types").FourthParty|null|undefined} fourthParty
 * @returns {boolean}
 */
export function isFourthPartyActive(fourthParty) {
  return Boolean(fourthParty && fourthParty.role);
}

/**
 * Which of a company's contacts the 4th-party Contact dropdown should start on: the main
 * (preferred) contact if the repository flags one, otherwise the first contact it returned.
 * Mirrors Professional Contacts' `defaultContactId(firm)` — same rule, different source shape.
 * @param {import("./provision.types").ContactDetail[]|null|undefined} contacts
 * @returns {?string} the contact id to pre-select, or null when the company has no contacts
 */
export function defaultFourthPartyContactId(contacts) {
  if (!Array.isArray(contacts) || contacts.length === 0) return null;
  const main = contacts.find((contact) => contact && contact.isMainContact);
  return (main || contacts[0]).id || null;
}

/**
 * The contact number to show for a chosen 4th-party contact. For an EXISTING repository contact
 * the number is read from the repository (contract: FourthParty.contactNumber "Read from
 * repository for an existing contact"); the locally-typed value is only used while adding a new
 * one, so it is the fallback rather than the winner.
 * @param {import("./provision.types").ContactDetail|null|undefined} contact
 * @param {?string} [fallback] the number already held on the draft (or typed for a new contact)
 * @returns {?string}
 */
export function fourthPartyContactNumber(contact, fallback) {
  return (contact && contact.contactNumber) || fallback || null;
}

// ---------------------------------------------------------------------------------------
// Notes (DFXX12)
// ---------------------------------------------------------------------------------------

export const NOTES_MAX_LENGTH = 2000;

/** @param {string|null|undefined} notes */
export function isValidNotes(notes) {
  return !notes || notes.length <= NOTES_MAX_LENGTH;
}

// ---------------------------------------------------------------------------------------
// Provision Details draft validation (single summary, matching the Professional Contacts
// add/edit convention rather than scattering field-level checks in JSX)
// ---------------------------------------------------------------------------------------

/**
 * @param {import("./provision.types").ProvisionDetailsInput} details
 * @param {string|null|undefined} notes
 * @returns {{valid: boolean, missing: string[]}}
 */
export function validateProvisionDraft(details, notes) {
  const missing = [];
  if (!isValidMoney(details?.provisionAmount)) missing.push("Provision Amount must be a valid £ amount (2 decimal places)");
  if (!isValidMoney(details?.gceNetOfProvision)) missing.push("GCE Net of Provision must be a valid £ amount (2 decimal places)");
  if (!isValidNotes(notes)) missing.push(`Notes must be ${NOTES_MAX_LENGTH} characters or fewer`);
  return { valid: missing.length === 0, missing };
}

// ---------------------------------------------------------------------------------------
// Dates — display as dd/mm/yyyy (UK), transport/store as ISO YYYY-MM-DD (golden rule #5)
// ---------------------------------------------------------------------------------------

/**
 * Formats an ISO date (or any Date-parseable value) for display, reusing the app's existing
 * date formatter/locale. Never returns the raw ISO string — callers must not roll their own.
 * @param {string|Date|null|undefined} isoDate
 * @returns {string} "dd/mm/yyyy", or "" when there is no date
 */
export function toDisplayDate(isoDate) {
  if (!isoDate) return "";
  return getFormattedDate(isoDate, "DD/MM/YYYY");
}

/**
 * Normalises a Date (as produced by A_DatePicker et al.) to the ISO YYYY-MM-DD the contract
 * transports. Never send a dd/mm/yyyy string on the wire.
 * @param {Date|string|null|undefined} value
 * @returns {string|null}
 */
export function toIsoDate(value) {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}
